'use client';

import {
    AspectRatio,
    Badge,
    Box,
    Flex,
    HStack,
    Text,
    VStack,
} from '@chakra-ui/react';
import { ICartItem, IRedux } from '@/_store/types';
import Image from 'next/image';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslations } from 'next-intl';
import {
    IComboGroup,
    ISavedModifier,
    IGroupModifier,
    ISingleModifier,
    IGroupModifierVariant,
    IDiscount,
} from '@/_types';
import { useQuery } from '@tanstack/react-query';
import productService from '@/_service/product.service';
import {
    ADD_COMMENT,
    DECREMENT,
    DELETE_COMMENT,
    INCREMENT,
    REMOVE,
} from '@/_store/cart/cart.slice';
import NumberToPrice from '@/_components/NumberToPrice';
import Counter from '@/_components/Counter';

import AddComment from '@/_components/AddComment';
import ConfirmModal from '@/_components/ConfirmModal';
import { useCustomLocale } from '@/_hooks/useCustomLocale';
import { QuestionIcon, TrashIcon } from '@/_assets/icon-components';
import { usePathname, useRouter } from '@/i18n/navigation';
import { Button } from '@/components/ui/button';
import useGTM from '@/_hooks/useGTM';
import { IProduct } from '@/entities/product/model/types';
import { useActiveBrand } from '@/_hooks/useActiveBrand';

interface IProps {
    shipperId: string;
    isBrand?: boolean;
    isCheckout?: boolean;
    size?: string;
    data?: IProduct;
    price?: number;
    index?: number;
    title?: string;
    readOnly?: boolean;
    cartItem: ICartItem;
    bonus_product?: {
        is_bonus: boolean;
        product: IDiscount['bonus_product'];
    };
    customEvents?: boolean;
    setCommentCustomComment?: any;
    customRemove?: (e: ICartItem) => void;
    customIncrement?: (e: ICartItem) => void;
    customDecrement?: (e: ICartItem) => void;
}

function CardRow({
    shipperId,
    isBrand,
    isCheckout = false,
    data,
    index,
    title,
    price,
    readOnly,
    cartItem,
    bonus_product,
    customEvents,
    customRemove = () => { },
    customIncrement = () => { },
    customDecrement = () => { },
}: IProps) {
    const [isDialog, setDialog] = useState(false);
    const [showFull, setShowFull] = useState(false);
    const [variantsInfo, setVariantsInfo] = useState<any[]>([]);
    const [modifiersData, setModifiersData] = useState<any[]>([]);
    const [orderModifiers, setOrderModifiers] = useState<ISavedModifier[]>([]);
    const [modifiersPrice, setModifiersPrice] = useState<number>(0);
    const [isModifiersMounted, setModifiersMounted] = useState(false);
    const [comment, setComment] = useState({
        isOpen: false,
        comment: '',
    });

    const t = useTranslations();
    const router = useRouter();
    const dispatch = useDispatch();
    const currentLocale = useCustomLocale();
    const pathName = usePathname();
    const { sendGTM } = useGTM();
    const cartState = useSelector((state: IRedux) => state.cart.cart);
    const { menu_id, is_hall } = useSelector((state: IRedux) => state.session);
    const { branches } = useSelector((state: IRedux) => state.common);
    const { data: settingsData } = useSelector((state: IRedux) => state.settings);
    // Barcha brand'lardagi productlarni yig'ish (brand ma'lumoti bilan)
    const branch = branches?.[shipperId]

    const cart = cartState?.[shipperId]?.products || [];

    const { data: comboData, error: comboError } = useQuery({
        queryKey: ['combo', data?.id],
        queryFn: () =>
            productService
                .getComboById(data?.id || '', {
                    menu_id:
                        is_hall === 'true'
                            ? menu_id !== 'null'
                                ? menu_id
                                : ''
                            : branch?.menu_id,
                })
                .then((res) => res?.data),
        enabled: Boolean(
            data?.type === 'combo' &&
            Object.keys(cartItem?.variants)?.length > 0 &&
            !!data?.id
        ),
    });

    const onIncrease = () => {
        if (customEvents) {
            customIncrement(cartItem);
        } else {
            const maxQty = cartItem?.max_qty || Infinity;

            if (cartItem?.quantity < maxQty) {
                const productWithBrand = cart.find(
                    (item: ICartItem) => item.key === cartItem.key
                );
                if (productWithBrand?.shipper_id) {
                    dispatch(
                        INCREMENT({ shipper_id: productWithBrand.shipper_id, key: cartItem.key })
                    );
                }
            }
        }
    };
    // Memoize the decrement handler
    const onDecrease = useCallback(() => {
        if (customEvents) {
            customDecrement(cartItem);
        } else {
            const minQty = cartItem?.min_qty || 1;
            const currentQty = cartItem?.quantity;
            const isOptional = cartItem?.is_optional;

            if (currentQty > minQty) {
                const productWithBrand = cart.find(
                    (item) => item.key === cartItem.key
                );
                if (productWithBrand?.shipper_id) {
                    dispatch(
                        DECREMENT({ shipper_id: productWithBrand.shipper_id, key: cartItem.key })
                    );
                }
            } else {
                setDialog(isOptional !== undefined ? isOptional : true);
            }
        }
    }, [
        customEvents,
        customDecrement,
        cartItem?.key,
        cartItem?.quantity,
        cartItem?.min_qty,
        cartItem?.is_optional,
        cart,
        dispatch,
    ]);

    const onRemove = useCallback(() => {
        if (typeof dataLayer !== 'undefined') {
            dataLayer?.push({ ecommerce: null });
        }
        sendGTM('remove_from_cart', {
            cart: [cartItem],
            name: settingsData?.data?.shipper_name,
        });

        const willBeEmpty = cart?.length === 1;

        // Remove the item
        if (customEvents) {
            customRemove(cartItem);
        } else {
            const productWithBrand = cart.find(
                (item: ICartItem) => item.key === cartItem.key
            );

            if (productWithBrand?.shipper_id) {
                dispatch(REMOVE({ shipper_id: productWithBrand.shipper_id, key: cartItem.key }));
            }
        }

        if (willBeEmpty && pathName?.includes('checkout')) {
            router.push('/');
        }
    }, [
        customEvents,
        customRemove,
        cartItem,
        cart,
        dispatch,
        router,
        cart?.length,
    ]);

    // Combo variants
    useEffect(() => {
        if (comboData && cartItem?.variants) {
            // If products is combo, get info about variants
            let variantMap: any[] = [];
            comboData?.groups?.map((group: IComboGroup) => {
                if (cartItem?.variants?.[group?.id]) {
                    group.variants.map((variant) => {
                        if (variant.id === cartItem?.variants?.[group?.id]?.variant_id) {
                            variantMap.push({ ...variant, group_id: group?.id });
                        }
                    });
                }
            });
            setVariantsInfo(variantMap);
        }
    }, [comboData, cartItem]);

    useEffect(() => {
        if (data?.modifiers) {
            let modifiersTitle: {
                id: string;
                title: { ru: string; uz: string; en: string };
                price: number;
                quantity: number;
            }[] = [];

            // Helper function to get modifier from cartItem directly (no need to search in cart)
            const getModifier = (modifierId: string): ISavedModifier | undefined => {
                if (!cartItem?.order_modifiers) return undefined;
                return cartItem.order_modifiers.find(
                    (mod: ISavedModifier) =>
                        mod.modifier_id === modifierId && mod.modifier_quantity > 0
                );
            };

            if (data?.modifiers?.single_modifiers) {
                const value = data?.modifiers?.single_modifiers;
                if (value) {
                    value.forEach((mod: ISingleModifier) => {
                        const modifierInCart = getModifier(mod.id);
                        if (
                            modifierInCart?.modifier_id &&
                            modifierInCart?.modifier_quantity > 0
                        ) {
                            modifiersTitle.push({
                                id: mod.id,
                                title: mod.name,
                                price: mod.price,
                                quantity: modifierInCart?.modifier_quantity,
                            });
                        }
                    });
                }
            }
            if (data?.modifiers?.group_modifiers) {
                const value = data?.modifiers?.group_modifiers;
                if (value) {
                    value.forEach((mod: IGroupModifier) =>
                        mod?.variants?.forEach((variant: IGroupModifierVariant) => {
                            const modifierInCart = getModifier(variant.id);
                            if (
                                modifierInCart?.modifier_id &&
                                modifierInCart?.modifier_quantity > 0
                            ) {
                                modifiersTitle.push({
                                    id: variant.id,
                                    title: variant.title,
                                    price: variant.out_price,
                                    quantity: modifierInCart.modifier_quantity,
                                });
                            }
                        })
                    );
                }
            }

            setModifiersData(modifiersTitle);
        }
    }, [data?.modifiers, cartItem?.order_modifiers, cartItem?.key]);

    useEffect(() => {
        if (orderModifiers?.length > 0) {
            let sum = 0;
            for (const modifier of orderModifiers) {
                sum += modifier.modifier_price * modifier.modifier_quantity;
            }
            setModifiersPrice(sum);
        }
    }, [orderModifiers]);

    useEffect(() => {
        if (cartItem.order_modifiers?.length > 0 && !isModifiersMounted) {
            setOrderModifiers(cartItem.order_modifiers);
            setModifiersMounted(true);
        }
    }, [cartItem.order_modifiers, isModifiersMounted]);

    useEffect(() => {
        if (comboError) {
            const productWithBrand = cart.find(
                (item: ICartItem) => item.key === cartItem.key
            );
            if (productWithBrand?.shipper_id) {
                dispatch(REMOVE({ shipper_id: productWithBrand.shipper_id, key: cartItem.key }));
            }
        }
    }, [comboError, dispatch, cartItem?.key, cart]);

    // const toggleDrawer = () => {
    //   setComment((prevState) => ({
    //     ...prevState,
    //     isOpen: !prevState.isOpen,
    //   }))
    // }
    const handleSaveComment = () => {
        const productWithBrand = cart.find(
            (item: ICartItem) => item.key === cartItem.key
        );
        if (productWithBrand?.shipper_id) {
            dispatch(
                ADD_COMMENT({
                    shipper_id: productWithBrand.shipper_id,
                    key: cartItem.key,
                    comment: comment.comment,
                })
            );
        }
        setComment((prevState) => ({
            ...prevState,
            isOpen: false,
            comment: '',
        }));
    };

    const renderText = useMemo(() => {
        let fullText = '';

        if (variantsInfo.length > 0) {
            fullText += variantsInfo
                .map(
                    (variant, idx) =>
                        `${variant.title?.[currentLocale]} (${cartItem?.variants?.[idx]?.variant_id === variant.id
                            ? cartItem?.variants?.[idx]?.quantity
                            : ''
                        } ${t('pcs')})`
                )
                .join(', ');
        }

        if (modifiersData?.length > 0) {
            fullText +=
                (fullText ? ', ' : '') +
                modifiersData
                    .map(
                        (modifier) =>
                            `${modifier.title?.[currentLocale]} (${modifier.quantity} ${t(
                                'pcs'
                            )})`
                    )
                    .join(', ');
        }

        if (!data?.modifiers && !variantsInfo.length && data?.description) {
            fullText += (fullText ? ', ' : '') + data?.description[currentLocale];
        }

        return fullText;
    }, [variantsInfo, modifiersData, data?.description, currentLocale, cartItem?.variants]);

    const handleDeleteComment = () => {
        const productWithBrand = cart.find(
            (item: ICartItem) => item.key === cartItem.key
        );
        if (productWithBrand?.shipper_id) {
            dispatch(
                DELETE_COMMENT({ shipper_id: productWithBrand.shipper_id, key: cartItem.key })
            );
        }
        setComment((prevState) => ({
            ...prevState,
            isOpen: false,
        }));
    };

    return (
        <Box>
            <Flex
                gap={3}
                alignItems='center'
                justifyContent='space-between'
                position='relative'
            >
                <HStack top={1} left={1} zIndex={1} position='absolute'>
                    {cartItem?.discount_percent !== 0 && (
                        <Badge
                            rounded={'full'}
                            variant='solid'
                            colorPalette='green'
                            fontSize='8px'
                            lineHeight={1}
                            size='xs'
                        >
                            {cartItem?.discount_percent} %
                        </Badge>
                    )}
                </HStack>
                <AspectRatio
                    minW={{ base: '60px', lg: isBrand ? '48px' : '64px' }}
                    bg={'background'}
                    ratio={1 / 1}
                    overflow='hidden'
                    rounded={isBrand ? 8 : 16}
                >
                    {data?.image || bonus_product?.product?.image ? (
                        <Image
                            src={`${process.env.BASE_URL}${data?.image || bonus_product?.product?.image
                                }`}
                            alt={`${data?.title?.[currentLocale]} - photo ${index}`}
                            style={{ objectFit: 'cover', fontSize: '8px' }}
                            fill
                        />
                    ) : (
                        <Box>
                            <QuestionIcon size={isBrand ? 28 : 32} />
                        </Box>
                    )}
                </AspectRatio>
                <VStack
                    flex={1}
                    gap='6px'
                    justifyContent='start'
                    alignItems='start'
                    lineHeight={1.2}
                    fontSize={{ base: 'sm', lg: isBrand ? 'sm' : 'md' }}
                >
                    <Text
                        fontSize='sm'
                        color='text'
                        fontWeight={{ base: 600, lg: isBrand ? 600 : 400 }}
                    >
                        {data?.title?.[currentLocale] || title || cartItem?.product_name}
                    </Text>
                    {renderText && (
                        <Text
                            color='gray.600'
                            fontSize='xs'
                            lineClamp={showFull ? undefined : 1}
                            onClick={() => setShowFull(!showFull)}
                        >
                            {renderText}
                        </Text>
                    )}

                    {renderText?.length > 80 && (
                        <Text
                            w='fit-content'
                            fontSize='xs'
                            cursor={'pointer'}
                            fontWeight={400}
                            _hover={{ color: 'primary' }}
                            transform={`translateY(${showFull ? 0 : -5}px)`}
                            onClick={() => setShowFull(!showFull)}
                            mt={1}
                        >
                            {showFull ? t('show_less') : t('show_more')}
                        </Text>
                    )}
                    <NumberToPrice
                        value={
                            price ? price : cartItem?.price_with_discount + modifiersPrice
                        }
                        textProps={{
                            color: 'text',
                            fontWeight: 500,
                        }}
                        spanProps={{
                            color: 'text',
                            fontWeight: 500,
                            fontSize: { base: 'sm', lg: isBrand ? 'sm' : 'md' },
                        }}
                    />
                </VStack>

                <Box>
                    {cartItem?.in_stop ? (
                        <HStack alignItems='end' flexDir={'column'} gap={0}>
                            <Button
                                color={'red'}
                                px={2}
                                size='xs'
                                variant={'outline'}
                                colorPalette={'red'}
                                rounded={8}
                                fontWeight={500}
                                onClick={onRemove}
                            >
                                <TrashIcon color='red' />
                                {t('remove')}
                            </Button>
                            <Text fontSize={'xs'} color={'gray.700'}>
                                {t('out_of_stock')}
                            </Text>
                        </HStack>
                    ) : (
                        <Counter
                            isBrand={isBrand}
                            width={isCheckout ? '108px' : 'full'}
                            height={{ base: '32px', lg: isBrand ? '32px' : '48px' }}
                            bg={'background'}
                            variable={cartItem?.quantity}
                            onDecrease={onDecrease}
                            onIncrease={onIncrease}
                            isDisabled={readOnly}
                        />
                    )}
                </Box>
            </Flex>
            {!settingsData?.data?.disable_product_comments && (
                <Box>
                    {!bonus_product?.is_bonus &&
                        !cartItem?.default_product &&
                        (!cartItem?.comment ? (
                            <Text
                                mt={3}
                                fontWeight={500}
                                cursor='pointer'
                                fontSize='sm'
                                float='right'
                                color='primary'
                                display={cartItem?.in_stop ? 'none' : 'block'}
                                onClick={() => {
                                    !cartItem?.in_stop &&
                                        setComment((prevState) => ({
                                            ...prevState,
                                            isOpen: true,
                                        }));
                                }}
                                opacity={cartItem?.in_stop ? 0.5 : 1}
                            >
                                {t('leave_comment')}
                            </Text>
                        ) : (
                            <VStack
                                p={4}
                                mt={4}
                                gap={2}
                                w='full'
                                rounded={16}
                                fontSize='sm'
                                color={'text'}
                                bg='background'
                            >
                                <Flex
                                    w='full'
                                    fontWeight={500}
                                    alignItems='center'
                                    justifyContent='space-between'
                                >
                                    <Text>{t('comment')}</Text>
                                    <Text
                                        onClick={handleDeleteComment}
                                        color='fg.error'
                                        cursor='pointer'
                                    >
                                        {t('remove')}
                                    </Text>
                                </Flex>
                                <Text w='full'>{cartItem?.comment}</Text>
                            </VStack>
                        ))}
                </Box>
            )}
            <AddComment
                onOpenChange={() =>
                    setComment((prevState) => ({
                        ...prevState,
                        isOpen: false,
                    }))
                }
                setComment={setComment}
                comment={comment}
                handleSaveComment={handleSaveComment}
            />
            <ConfirmModal
                isOpen={isDialog}
                onConfirm={onRemove}
                title={t('attention')}
                onClose={() => setDialog(false)}
                description={t('are_you_sure-product')}
            />
        </Box>
    );
}

export default memo(CardRow);
